import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useAuth0 } from '@auth0/auth0-react';
import _ from 'lodash';
import moment from 'moment';
import {
  IComboBoxOption,
  PrimaryButton,
  Stack,
  StackItem,
  Toggle,
} from '@fluentui/react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { throwError, SeverityLevel } from '../../redux/error/errorSlice';
import {
  convertEmployeesToComboBoxOptions,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_LOADING_MESSAGE,
  default_page_size,
  DEFAULT_TOAST_DURATION,
  DEFAULT_TOAST_POSITION,
  GET_EMPLOYEES,
  IEmployee,
  stackTokens15,
} from '../../utils';
import { GET_LOG_ITEMS, ILogItem } from '../../utils/LogItem';
import { ComboboxWithFilter } from '../../components/parts';
import useDebounce from '../../components/hooks/useDebounce';
import DateView from '../../components/parts/DateView';
import { getPermissions } from '../../redux/permission/permissionSlice';
import { toastError, toastSuccess } from '../../utils/toast';
import PageHeader from '../../components/parts/PageHeader';
import LogItemsOverview from './components/LogItemsOverview';

interface IPagedLogItem {
  logItems: ILogItem[];
  skip: number;
  take: number;
  filter: string;
  initialLoad: boolean;
  showLoadMore: boolean;
  lazyLoading: boolean;
  isFiltered: boolean;
  isSortedAsc: boolean;
  sortedField: string;
}
//
const LogItems = () => {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const dispatch = useAppDispatch();
  //

  const permissions = useAppSelector(getPermissions);
  const [pagedState, setPagedState] = useState<IPagedLogItem>({
    logItems: [],
    skip: 0,
    take: default_page_size,
    filter: '',
    showLoadMore: true,
    lazyLoading: true,
    isFiltered: false,
    initialLoad: true,
    isSortedAsc: true,
    sortedField: 'action_required_by',
  });

  const [dateFilter, setDateFilter] = useState({
    start_date: moment().subtract(1, 'year').toISOString(),
    end_date: '',
  });
  const [actionRequired, setActionRequired] = useState(true);
  const [currentUser, setCurrentUser] = useState<IEmployee>();
  const [salesRepFilter, setSalesRepFilter] = useState<number | undefined>();

  const { data: salesRepsData } = useQuery(GET_EMPLOYEES, {
    variables: {
      filter: {
        sales_rep: true,
      },
    },
    skip:
      !user ||
      !(
        (user as any)['3bouw/roles'].includes('SuperAdmin') ||
        (user as any)['3bouw/roles'].includes('Directie') ||
        (user as any)['3bouw/roles'].includes('LogItemAdmin')
      ),
  });

  useQuery(GET_EMPLOYEES, {
    variables: {
      filter: {
        app_user: {
          auth0_id: {
            equals: user && user.sub,
          },
        },
      },
    },
    skip: !user || !user.sub,
    onCompleted: x => {
      if (x && x.findManyEmployees && x.findManyEmployees.length > 0) {
        setCurrentUser(x.findManyEmployees[0]);
        setSalesRepFilter(x.findManyEmployees[0].id);
      }
    },
  });

  const debouncedDate = useDebounce(dateFilter, 500);

  useEffect(() => {
    setPagedState(prevState => ({ ...prevState, skip: 0, logItems: [] }));
  }, [dateFilter, actionRequired, salesRepFilter]);

  const getOrderBy = () => {
    const orderByObject: any = {}; // deliberately kept any as type to index by string

    if (pagedState.sortedField === 'sales_rep') {
      orderByObject.sales_rep = {
        first_name: pagedState.isSortedAsc ? 'asc' : 'desc',
      };
    } else if (pagedState.sortedField === 'lead') {
      orderByObject.lead = {
        first_name1: pagedState.isSortedAsc ? 'asc' : 'desc',
      };
    } else {
      orderByObject[pagedState.sortedField] = pagedState.isSortedAsc
        ? 'asc'
        : 'desc';
    }

    return orderByObject;
  };

  const getFilters = (
    dateFilter: { end_date: string; start_date: string },
    actionRequired: boolean,
  ) => {
    const filterObject: any = {
      AND: [
        { action_required: actionRequired },
        {
          sales_rep: salesRepFilter
            ? {
                id: {
                  equals: salesRepFilter,
                },
              }
            : undefined,
          action_required_by:
            dateFilter.start_date && !dateFilter.end_date
              ? { gte: dateFilter.start_date }
              : dateFilter.end_date && !dateFilter.start_date
              ? { lte: dateFilter.end_date }
              : {},
          AND:
            dateFilter.start_date && dateFilter.end_date
              ? [
                  {
                    action_required_by: {
                      gte: moment(
                        new Date(dateFilter.start_date),
                      ).toISOString(),
                    },
                  },
                  {
                    action_required_by: {
                      lte: moment(new Date(dateFilter.end_date)).toISOString(),
                    },
                  },
                ]
              : [],
        },
      ],
    };

    return filterObject;
  };

  const downloadFile = useCallback(async () => {
    const res = await toast.promise(
      // eslint-disable-next-line no-async-promise-executor
      new Promise(async (resolve, reject) => {
        const url = `${process.env.REACT_APP_BACKEND_URI}/files/logitems/xls`;
        const token = await getAccessTokenSilently();
        const fileName = `Logitems${
          salesRepFilter
            ? `_verkoper_${
                salesRepsData?.findManyEmployees?.find(
                  (x: IEmployee) => x.id === salesRepFilter,
                )?.first_name
              }`
            : '_all'
        }_${moment().format('YYYYMMDD')}.xlsx`;
        const res = await fetch(url, {
          method: 'post',
          headers: new Headers({
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            filter: getFilters(debouncedDate, actionRequired),
            sales_rep_id: salesRepFilter,
          }),
        });

        const result = await res.blob();

        const aElement = document.createElement('a');
        aElement.setAttribute('download', fileName);
        const href = URL.createObjectURL(result);
        aElement.href = href;
        aElement.setAttribute('target', '_blank');
        aElement.click();
        URL.revokeObjectURL(href);

        resolve(true);
      }),
      {
        pending: {
          position: DEFAULT_TOAST_POSITION,
          render() {
            return DEFAULT_LOADING_MESSAGE;
          },
        },
      },
      {
        autoClose: DEFAULT_TOAST_DURATION,
      },
    );

    await res;

    toastSuccess('Bestand is gedownload');
  }, [dateFilter, salesRepFilter, actionRequired, debouncedDate]);

  const {
    data: logItemsData,
    loading,
    error,
    fetchMore,
    refetch,
  } = useQuery(GET_LOG_ITEMS, {
    // Geen client.cache.reset() meer bij zoeken/sorteren: elke filter + sortering heeft een eigen
    // cache-ingang (skipTakePagination in App.tsx). Eerst tonen wat in de cache zit en altijd
    // verversen bij de server; daarna cache-first, zodat "meer laden" geen extra verversing uitlokt.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      filter: getFilters(debouncedDate, actionRequired), // inUse == active?
      take: pagedState.take,
      skip: 0,
      orderBy: getOrderBy(),
    },
    onError: error => {
      toastError(error.message || DEFAULT_ERROR_MESSAGE);
    },
  });

  const onChangeFilterActionRequired = () => {
    setActionRequired(!actionRequired);
  };

  const setSorting = (isSortedAsc: boolean, sortedField: string) => {
    setPagedState(prevState => ({
      ...prevState,
      isSortedAsc,
      sortedField,
    }));
  };

  const fetchMoreLogItems = () => {
    // verder vanaf wat er getoond wordt (zoals bij leads/klanten)
    const currentCount = logItemsData?.findManyLogItems?.length || 0;
    // Load more guard

    fetchMore({
      variables: {
        filter: getFilters(debouncedDate, actionRequired),
        take: pagedState.take,
        skip: currentCount,
        orderBy: getOrderBy(),
      },
    });

    setPagedState(prevState => ({
      ...prevState,
      skip: currentCount,
    }));
  };

  if (!isAuthenticated) return <p>Verboden</p>;
  if (error) return <p>Oeps, er ging iets mis...</p>;

  return (
    <Stack tokens={stackTokens15}>
      <div style={{ padding: '25px' }}>
        <PageHeader
          title='Logitems'
          iconName='RecurringTask'
          loading={loading}
        />

        {/* <TextField
        label='Zoeken...'
        value={filter}
        styles={textFieldStyles300}
        onChange={onSearchValueChange}
      /> */}
        {((user as any)['3bouw/roles'].includes('SuperAdmin') ||
          (user as any)['3bouw/roles'].includes('Directie') ||
          (user as any)['3bouw/roles'].includes('LogItemAdmin')) && (
          <Stack style={{ flexDirection: 'row' }}>
            {salesRepsData &&
              salesRepsData.findManyEmployees &&
              salesRepsData.findManyEmployees.length > 0 && (
                <StackItem style={{ width: 300, marginRight: 25 }}>
                  <ComboboxWithFilter
                    label='Verkoper'
                    options={convertEmployeesToComboBoxOptions(
                      salesRepsData.findManyEmployees,
                    )}
                    value={salesRepFilter}
                    callBack={(newValue: IComboBoxOption[]) => {
                      if (newValue && newValue.length > 0) {
                        setSalesRepFilter(newValue[0].key as number);
                      }
                    }}
                  />
                </StackItem>
              )}

            <StackItem style={{ paddingTop: 29 }}>
              {currentUser && (
                <PrimaryButton
                  style={{ marginRight: 20 }}
                  onClick={() => {
                    setSalesRepFilter(currentUser.id);
                  }}
                >
                  Mijn logitems
                </PrimaryButton>
              )}

              <PrimaryButton
                onClick={() => {
                  setSalesRepFilter(undefined);
                }}
              >
                Filter leegmaken
              </PrimaryButton>
            </StackItem>
          </Stack>
        )}

        <Stack style={{ flexDirection: 'row' }}>
          <Stack style={{ flexDirection: 'row', marginRight: 20 }}>
            <Stack style={{ marginBottom: 5, marginRight: 15 }}>
              <DateView
                item={dateFilter}
                setItem={setDateFilter}
                date={dateFilter && dateFilter.start_date}
                label='Startdatum'
                field='start_date'
              />
            </Stack>

            <Stack style={{ marginBottom: 5 }}>
              <DateView
                item={dateFilter}
                setItem={setDateFilter}
                date={dateFilter && dateFilter.end_date}
                label='Einddatum'
                field='end_date'
              />
            </Stack>
          </Stack>

          <Toggle
            label='Actie nodig?'
            defaultChecked={actionRequired}
            onText='Actie nodig'
            offText='Alles tonen'
            onChange={onChangeFilterActionRequired}
          />
        </Stack>
      </div>

      <LogItemsOverview
        logItems={
          logItemsData && logItemsData.findManyLogItems
            ? logItemsData.findManyLogItems
            : []
        }
        loading={loading}
        setSorting={setSorting}
        isSortedAsc={pagedState.isSortedAsc}
        sortedField={pagedState.sortedField}
        showLoadMore={pagedState.showLoadMore}
        loadMore
        loadMoreCallback={fetchMoreLogItems}
        lazyLoading={pagedState.lazyLoading}
        downloadFile={downloadFile}
        refetchLogItems={refetch}
      />
    </Stack>
  );
};

export default LogItems;
